import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Heart, Users, Utensils, Gift, BookOpen, Star } from 'lucide-react';

export default function RatgeberEidAdhaGeschenke() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#4A5D4F]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link
            to="/ratgeber"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Alle Ratgeber
          </Link>
          <div className="flex items-center gap-2 text-[#D4C5A9] text-sm mb-4">
            <Calendar className="w-4 h-4" />
            <span>Aktualisiert: Februar 2026</span>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6 leading-tight">
            Eid ul Adha Geschenkideen: Bedeutungsvolle Präsente zum Opferfest
          </h1>
          <p className="text-lg text-white/80 leading-relaxed">
            Alles, was Sie über das Schenken zu Eid ul Adha wissen müssen – Geschichte, Traditionen 
            und die schönsten Geschenkideen für das Opferfest 2026.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          
          {/* Introduction */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-[#2C332E] leading-relaxed mb-8">
              Eid ul Adha, auch bekannt als das Opferfest oder "Kurban Bayramı", ist einer der wichtigsten 
              Feiertage im Islam. Es erinnert an die Hingabe und den Glauben des Propheten Ibrahim (Abraham) 
              und findet jährlich am 10. Tag des islamischen Monats Dhul Hiddscha statt. In diesem umfassenden 
              Guide erfahren Sie alles über die Bedeutung des Festes und finden die perfekten Geschenkideen.
            </p>

            {/* Table of Contents */}
            <div className="bg-[#F5F5F0] rounded-2xl p-6 md:p-8 mb-12">
              <h2 className="font-serif text-xl font-medium text-[#2C332E] mb-4">
                📖 Inhaltsverzeichnis
              </h2>
              <ul className="space-y-2 text-[#4A5D4F]">
                <li><a href="#bedeutung" className="hover:underline">1. Die Bedeutung von Eid ul Adha</a></li>
                <li><a href="#datum" className="hover:underline">2. Wann ist Eid ul Adha 2026?</a></li>
                <li><a href="#traditionen" className="hover:underline">3. Traditionen und Bräuche</a></li>
                <li><a href="#schenken" className="hover:underline">4. Warum wir zu Eid ul Adha schenken</a></li>
                <li><a href="#geschenkideen" className="hover:underline">5. Die besten Geschenkideen</a></li>
                <li><a href="#unterschied" className="hover:underline">6. Unterschied zu Eid ul Fitr</a></li>
                <li><a href="#tipps" className="hover:underline">7. Geschenk-Etikette zu Eid ul Adha</a></li>
              </ul>
            </div>

            {/* Section 1 - Bedeutung */}
            <section id="bedeutung" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  1. Die Bedeutung von Eid ul Adha
                </h2>
              </div>
              
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ul Adha (arabisch: عيد الأضحى) bedeutet wörtlich "Fest der Opferung". Es erinnert an die 
                Geschichte des Propheten Ibrahim, der bereit war, seinen Sohn Ismail auf Gottes Befehl zu opfern. 
                Als Allah Ibrahims Hingabe und Gehorsam sah, sandte er einen Widder als Ersatz für das Opfer.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Diese Geschichte lehrt uns wichtige Werte: <strong>Glaube, Hingabe, Gehorsam gegenüber Allah 
                und Vertrauen in Gottes Plan</strong>. Das Fest ist eine Zeit, in der Muslime weltweit diese 
                Werte reflektieren und ihre Dankbarkeit zum Ausdruck bringen.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ul Adha fällt zusammen mit der jährlichen Hadsch-Pilgerfahrt nach Mekka, eine der fünf 
                Säulen des Islam. Während Millionen von Muslimen die Pilgerfahrt vollziehen, feiern Muslime 
                auf der ganzen Welt dieses besondere Fest mit Gebeten, Opfergaben und dem Teilen von Freude 
                mit Familie und Bedürftigen.
              </p>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">Die spirituelle Bedeutung</h4>
                <ul className="text-[#4B5563] space-y-2">
                  <li>• <strong>Tawakkul (Gottvertrauen)</strong> – Ibrahim vertraute Allah vollkommen</li>
                  <li>• <strong>Taqwa (Gottesfurcht)</strong> – Die Bereitschaft, Allahs Gebote zu befolgen</li>
                  <li>• <strong>Großzügigkeit</strong> – Das Opferfleisch wird mit Bedürftigen geteilt</li>
                  <li>• <strong>Gemeinschaft</strong> – Familien und Nachbarn kommen zusammen</li>
                </ul>
              </div>
            </section>

            {/* Section 2 - Datum */}
            <section id="datum" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  2. Wann ist Eid ul Adha 2026?
                </h2>
              </div>

              <div className="bg-[#4A5D4F] text-white rounded-xl p-6 mb-6">
                <p className="text-lg font-medium mb-2">Eid ul Adha 2026</p>
                <p className="text-3xl font-serif mb-2">27. Mai 2026</p>
                <p className="text-white/70 text-sm">
                  * Das genaue Datum kann je nach Mondsichtung um einen Tag variieren
                </p>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das Fest dauert traditionell vier Tage (10. bis 13. Dhul Hiddscha). In Deutschland haben 
                viele muslimische Gemeinden besondere Gottesdienste am ersten Tag, gefolgt von Familienfeiern 
                und dem traditionellen Opferritual (Qurbani).
              </p>
              <p className="text-[#4B5563] leading-relaxed">
                <strong>Tipp:</strong> Bestellen Sie Ihre Eid-Geschenke mindestens 2 Wochen vor dem Fest, 
                um rechtzeitig vorbereitet zu sein. Besonders beliebte Artikel können schnell ausverkauft sein.
              </p>
            </section>

            {/* Section 3 - Traditionen */}
            <section id="traditionen" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  3. Traditionen und Bräuche
                </h2>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Das Eid-Gebet</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Der Tag beginnt früh mit dem Eid-Gebet (Salat al-Eid) in der Moschee oder auf einem offenen 
                Platz. Muslime ziehen ihre beste Kleidung an und versammeln sich zum gemeinsamen Gebet, 
                gefolgt von der Khutba (Predigt) des Imams.
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Das Opferritual (Qurbani)</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Nach dem Gebet führen diejenigen, die finanziell dazu in der Lage sind, das Opferritual durch. 
                Das Fleisch des Opfertieres (meist ein Schaf, eine Ziege oder ein Rind) wird traditionell in 
                drei Teile geteilt:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Ein Drittel</strong> für die eigene Familie</li>
                <li><strong>Ein Drittel</strong> für Verwandte, Freunde und Nachbarn</li>
                <li><strong>Ein Drittel</strong> für Bedürftige und Arme</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Familienbesuche und Geschenke</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Die Tage nach dem Eid-Gebet sind geprägt von Familienbesuchen. Ältere Verwandte werden zuerst 
                besucht, um Respekt zu zeigen. Kinder erhalten traditionell "Eidi" (Geldgeschenke) und kleine 
                Präsente.
              </p>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Festliche Mahlzeiten</h3>
              <p className="text-[#4B5563] leading-relaxed">
                Eid ul Adha ist auch ein kulinarisches Fest. Familien kochen traditionelle Gerichte mit dem 
                Opferfleisch – von Biryani über Kebabs bis hin zu Suppen und Eintöpfen. Das gemeinsame Essen 
                stärkt die familiären Bande.
              </p>
            </section>

            {/* Section 4 - Warum schenken */}
            <section id="schenken" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  4. Warum wir zu Eid ul Adha schenken
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-4">
                Das Schenken zu Eid ul Adha hat eine tiefe Bedeutung, die über materielle Gaben hinausgeht. 
                Es ist ein Ausdruck von:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🤝 Großzügigkeit</h4>
                  <p className="text-sm text-[#4B5563]">Das Teilen von Segen mit anderen, inspiriert durch die Qurbani-Tradition</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">❤️ Liebe & Verbundenheit</h4>
                  <p className="text-sm text-[#4B5563]">Zeigen Sie Ihren Liebsten, dass Sie an sie denken</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">🙏 Dankbarkeit</h4>
                  <p className="text-sm text-[#4B5563]">Anerkennung für Allahs Segen und die Gemeinschaft</p>
                </div>
                <div className="bg-[#F5F5F0] rounded-xl p-5">
                  <h4 className="font-medium text-[#2C332E] mb-2">👨‍👩‍👧‍👦 Tradition bewahren</h4>
                  <p className="text-sm text-[#4B5563]">Weitergabe islamischer Werte an die nächste Generation</p>
                </div>
              </div>

              <p className="text-[#4B5563] leading-relaxed">
                Der Prophet Muhammad (Friede sei mit ihm) ermutigte zum Schenken: "Tauscht Geschenke aus, 
                denn sie festigen die Liebe zwischen euch." Ein durchdachtes Geschenk zu Eid ul Adha zeigt 
                Wertschätzung und stärkt die Beziehungen innerhalb der Familie und Gemeinschaft.
              </p>
            </section>

            {/* Section 5 - Geschenkideen */}
            <section id="geschenkideen" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  5. Die besten Geschenkideen zu Eid ul Adha
                </h2>
              </div>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Kulinarische Geschenke</h3>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Da Eid ul Adha eng mit dem Teilen von Fleisch verbunden ist, sind kulinarische Geschenke 
                besonders passend:
              </p>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Premium Gewürz-Sets</strong> – Für die Zubereitung traditioneller Eid-Gerichte</li>
                <li><strong>Hochwertige Datteln</strong> – Ein klassisches und immer willkommenes Geschenk</li>
                <li><strong>Orientalische Süßigkeiten</strong> – Baklava, Ma'amoul, Lokum</li>
                <li><strong>Tee- und Kaffee-Sets</strong> – Für die Gastfreundschaft</li>
                <li><strong>Grill-Zubehör</strong> – Perfekt für die Zubereitung des Qurbani-Fleisches</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Religiöse Geschenke</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Quran in schöner Ausgabe</strong> – Mit Übersetzung und Tafsir</li>
                <li><strong>Gebetszubehör</strong> – Hochwertige Gebetsteppiche, Tasbih-Ketten</li>
                <li><strong>Islamische Bücher</strong> – Über die Geschichte von Ibrahim und Ismail</li>
                <li><strong>Hadsch-Souvenirs</strong> – Zamzam-Wasser, Gebetsketten aus Mekka</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Geschenke für Kinder</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Kinderbücher über Eid ul Adha</strong> – Die Geschichte kindgerecht erzählt</li>
                <li><strong>Eid-Geschenkboxen</strong> – Mit Süßigkeiten und kleinen Spielzeugen</li>
                <li><strong>Eidi (Geldgeschenke)</strong> – Traditionell in schönen Umschlägen</li>
                <li><strong>Plüschtiere</strong> – Schaf-Kuscheltiere sind besonders passend!</li>
              </ul>

              <h3 className="font-serif text-xl font-medium text-[#2C332E] mb-4">Geschenke für Erwachsene</h3>
              <ul className="list-disc pl-6 text-[#4B5563] mb-6 space-y-2">
                <li><strong>Parfüm & Düfte</strong> – Oud, Attar und orientalische Düfte</li>
                <li><strong>Schmuck</strong> – Dezent und elegant</li>
                <li><strong>Festliche Kleidung</strong> – Traditionelle oder moderne Festkleidung</li>
                <li><strong>Spenden im Namen des Beschenkten</strong> – Eine besonders bedeutungsvolle Geste</li>
              </ul>

              <div className="bg-[#F5F5F0] rounded-xl p-6 my-6">
                <h4 className="font-medium text-[#2C332E] mb-3">💡 Besondere Idee: Sadaqah-Geschenk</h4>
                <p className="text-[#4B5563]">
                  Spenden Sie im Namen eines geliebten Menschen an eine Wohltätigkeitsorganisation. 
                  Diese spirituelle Geste verbindet die Tradition der Großzügigkeit zu Eid ul Adha mit 
                  einem bedeutungsvollen Geschenk, das beiden – dem Beschenkten und Bedürftigen – Freude bereitet.
                </p>
              </div>
            </section>

            {/* Section 6 - Unterschied */}
            <section id="unterschied" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#D4C5A9]/30 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  6. Unterschied zu Eid ul Fitr
                </h2>
              </div>

              <p className="text-[#4B5563] leading-relaxed mb-6">
                Beide Eid-Feste sind wichtige islamische Feiertage, unterscheiden sich aber in ihrer Bedeutung:
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse mb-6">
                  <thead>
                    <tr className="bg-[#4A5D4F] text-white">
                      <th className="p-4 text-left font-medium">Aspekt</th>
                      <th className="p-4 text-left font-medium">Eid ul Fitr</th>
                      <th className="p-4 text-left font-medium">Eid ul Adha</th>
                    </tr>
                  </thead>
                  <tbody className="text-[#4B5563]">
                    <tr className="border-b border-[#E5E7EB]">
                      <td className="p-4 font-medium">Bedeutung</td>
                      <td className="p-4">Fest des Fastenbrechens</td>
                      <td className="p-4">Opferfest</td>
                    </tr>
                    <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                      <td className="p-4 font-medium">Zeitpunkt</td>
                      <td className="p-4">Nach dem Ramadan</td>
                      <td className="p-4">Während der Hadsch-Zeit</td>
                    </tr>
                    <tr className="border-b border-[#E5E7EB]">
                      <td className="p-4 font-medium">Dauer</td>
                      <td className="p-4">3 Tage</td>
                      <td className="p-4">4 Tage</td>
                    </tr>
                    <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                      <td className="p-4 font-medium">Besonderheit</td>
                      <td className="p-4">Zakat ul-Fitr (Spende)</td>
                      <td className="p-4">Qurbani (Opferritual)</td>
                    </tr>
                    <tr className="border-b border-[#E5E7EB]">
                      <td className="p-4 font-medium">Geschenke</td>
                      <td className="p-4">Süßigkeiten, "Zuckerfest"-Tradition</td>
                      <td className="p-4">Fleisch teilen, kulinarische Gaben</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-[#4B5563] leading-relaxed">
                Zu beiden Festen sind Geschenke willkommen, aber die Art der Geschenke kann variieren. 
                Während zu Eid ul Fitr oft Süßigkeiten und leichte Gaben im Vordergrund stehen, passen 
                zu Eid ul Adha besonders kulinarische Geschenke und Präsente, die die Großzügigkeit des 
                Festes widerspiegeln.
              </p>
            </section>

            {/* Section 7 - Tipps */}
            <section id="tipps" className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#4A5D4F]/10 flex items-center justify-center">
                  <Utensils className="w-6 h-6 text-[#4A5D4F]" />
                </div>
                <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E]">
                  7. Geschenk-Etikette zu Eid ul Adha
                </h2>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">1</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Besuchen Sie Ältere zuerst</h4>
                    <p className="text-[#4B5563]">In der islamischen Tradition ist es respektvoll, zuerst die älteren Familienmitglieder zu besuchen und zu beschenken.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">2</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Vergessen Sie die Nachbarn nicht</h4>
                    <p className="text-[#4B5563]">Teilen Sie Freude auch mit nicht-muslimischen Nachbarn – das fördert den interkulturellen Dialog.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">3</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Denken Sie an Bedürftige</h4>
                    <p className="text-[#4B5563]">Die Tradition des Teilens ist zentral. Spenden Sie auch an Menschen, die nicht zur Familie gehören.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">4</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Verpacken Sie liebevoll</h4>
                    <p className="text-[#4B5563]">Eine schöne Verpackung mit "Eid Mubarak"-Karte macht jedes Geschenk besonders.</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 rounded-full bg-[#4A5D4F] text-white flex items-center justify-center flex-shrink-0 text-sm font-medium">5</div>
                  <div>
                    <h4 className="font-medium text-[#2C332E] mb-1">Die Intention zählt</h4>
                    <p className="text-[#4B5563]">Es geht nicht um den materiellen Wert – ein von Herzen kommendes Geschenk ist immer das wertvollste.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="font-serif text-2xl md:text-3xl font-medium text-[#2C332E] mb-6">
                Fazit: Ein gesegnetes Eid ul Adha
              </h2>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Eid ul Adha ist ein Fest der Hingabe, der Großzügigkeit und der Gemeinschaft. Das Schenken 
                zu diesem besonderen Anlass ist eine wunderbare Möglichkeit, diese Werte zu leben und 
                Freude zu verbreiten.
              </p>
              <p className="text-[#4B5563] leading-relaxed mb-4">
                Ob Sie traditionelle kulinarische Geschenke wählen, religiöses Zubehör schenken oder 
                im Namen eines geliebten Menschen spenden – das Wichtigste ist die Intention und die 
                Liebe, die Sie in Ihr Geschenk legen.
              </p>
              <p className="text-xl text-[#4A5D4F] font-medium">
                Eid Mubarak! Möge Allah Ihre Opfer annehmen und Sie mit Segen überschütten. 🐑
              </p>
            </section>

            {/* CTA */}
            <div className="bg-[#4A5D4F] rounded-2xl p-8 text-center">
              <h3 className="font-serif text-2xl font-medium text-white mb-4">
                Entdecken Sie unsere Eid ul Adha Geschenke
              </h3>
              <p className="text-white/80 mb-6">
                Handverlesene Präsente für ein unvergessliches Opferfest
              </p>
              <Link
                to="/eid-ul-adha"
                className="inline-flex items-center gap-2 bg-white text-[#4A5D4F] px-8 py-3 rounded-full font-medium hover:bg-[#D4C5A9] transition-colors"
              >
                Jetzt entdecken
                <Gift className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
